import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import { formatDate } from "@/lib/crm";
import { Bot, ArrowRight, Clapperboard } from "lucide-react";

export default function BriefingDialog({ open, onOpenChange, audition, prevAuditions }) {
  const { lang } = useAuth();
  const nav = useNavigate();
  const t = (k) => tr(k, lang);
  if (!audition) return null;

  const cdName = audition.casting_director_name || "—";
  const hasHistory = (prevAuditions?.length || 0) > 0;

  const goToLeo = () => {
    onOpenChange?.(false);
    // Land on the audition detail — user picks a scene to work on with Leo
    nav(`/crm/auditions/${audition.audition_id}`);
  };
  const backToList = () => {
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="aud-briefing"
        className="max-w-2xl bg-stone-950 border-stone-800 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="serif text-3xl text-stone-100">
            {t("brief.title")}
          </DialogTitle>
          <DialogDescription className="text-stone-400 text-sm">
            {t("brief.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {hasHistory ? (
            <div>
              <div className="label-cap mb-2">
                {t("brief.prev").replace("{name}", cdName)}
              </div>
              <ul className="space-y-2">
                {prevAuditions.map((p) => (
                  <li key={p.audition_id} className="paper !p-4">
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <div className="serif text-lg text-stone-100">{p.project}</div>
                      <div className="mono text-[10px] text-stone-500 uppercase tracking-widest">
                        {formatDate(p.created_at, lang)}
                      </div>
                    </div>
                    <div className="text-xs text-stone-400 mt-1 space-x-3">
                      {p.role && <span>{p.role}</span>}
                      <span className="text-amber-500">{t(`aud.status.${p.status}`)}</span>
                    </div>
                    {p.feedback && (
                      <div className="text-sm text-stone-300 mt-2 italic border-l-2 border-stone-700 pl-3">
                        {p.feedback}
                      </div>
                    )}
                    {p.notes && !p.feedback && (
                      <div className="text-xs text-stone-400 mt-2">{p.notes}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="paper p-6 text-stone-300 text-sm leading-relaxed">
              {t("brief.first").replace("{name}", cdName)}
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2 flex-wrap">
          <button
            onClick={backToList}
            data-testid="brief-back"
            className="ghost-btn"
          >
            <Clapperboard size={14} /> {t("brief.cta_back")}
          </button>
          <button
            onClick={goToLeo}
            data-testid="brief-go-leo"
            className="pill-btn"
          >
            <Bot size={14} /> {t("brief.cta_leo")} <ArrowRight size={14} />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
